import { useCallback, useEffect, useState } from 'react';
import { getBriefing, type BriefingDto } from '../../api/briefing';
// ★ 로케일을 못 박지 않는다 — 이 레포의 검사(`i18n-locale-hardcode.test.ts`)가 그것을 막는다.
import { getNumberLocale } from '../../lib/base-i18n';
import '../HrMenu/HrShell.css';
import './Briefing.css';

/**
 * 대표 업무보고 — **한 장으로 묻는 것들** (2026-09-01 · 파이프라인 하네스 판 I-2).
 *
 * ★ 주소가 `/exec` 였다가 **`/briefing`** 으로 바뀌었다(사용자 지시) — `exec` 는 개발자에게
 *   **실행(execute)** 으로 읽힌다.
 *
 * ## ★★★ 이 화면이 지키는 것
 *
 * 대표가 **판단의 근거로 삼는 숫자**다. 그래서 「없다」와 「모른다」를 절대 같게 그리지 않는다:
 *   · 연차 총합은 **부분합**이다 — 못 센 사람이 있으면 **몇 명을 뺐는지 함께 적는다**
 *   · 셀 수 없는 것(지출·매출·목표)은 **빈 그래프를 그리지 않고 «아직 없습니다» 라고 말한다**
 *
 * ## ★★ 왜 주소로만 들어오나
 *
 * 좌측 레일 메뉴의 실노출 SSOT 는 `admin_menu_registry`(DB)다. 코드에서 메뉴를 세우면
 * fallback 에만 뜨고 실제로는 안 뜬다 — **화면이 먼저, 메뉴는 그 다음**이라는 이 레포의
 * 순서를 따른다(전자결재 `/approval` 이 그렇게 열렸다).
 *
 * ## 재사용
 *
 * 생김새는 인사 화면의 지표 타일(`HrShell.css` 의 `.hrpage__kpi`)을 그대로 쓴다 — 새 디자인
 * 언어를 만들지 않는다.
 */

type State = 'loading' | 'ok' | 'forbidden' | 'failed';

const nf = (n: number) => n.toLocaleString(getNumberLocale());

/**
 * 분 → 「N시간 M분」. 0 이면 «0시간» — 0 은 «모른다» 가 아니라 값이다.
 *
 * ⛔ **이 자리는 «일부러» 공용 `formatWorkMinutes` 를 안 쓴다** (2026-09-26 결정). 두 가지를 잃는다:
 *   ① **단위 고정** — 이 함수가 그리는 곳은 KPI 칸 둘(승인 연장 · 예약 시간)이라 **단위가 「시간」으로
 *      고정**돼야 옆 칸과 나란히 읽힌다. 공용 함수로 바꾸면 0 이 「0분」, 30분이 「30분」이 되어
 *      **칸마다 단위가 갈린다**(위 한 줄이 이미 그 까닭을 적어 두었다).
 *   ② **천단위 구분** — 1,000시간을 넘는 회사 합계에서 「1,000시간」이 「1000시간」이 된다.
 * ⇒ 「여기도 모으면 되겠네」 하고 바꾸지 말 것.
 *    (정본 = `lib/daily-work.ts` 의 `formatWorkMinutes` · 면제 목록은 `lib/duration-format.test.ts`)
 */
function hoursText(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m === 0 ? `${nf(h)}시간` : `${nf(h)}시간 ${m}분`;
}

/**
 * 시간(소수) → 사람이 읽는 말.
 *
 * ★★ **「0시간」은 「측정 못 함」처럼 읽힌다.** 실제로 즉시 결재되는 회사에서는 평균이
 *    0.0x 시간이라 반올림하면 0 이 되는데, 그러면 이 화면의 규율(«없다»와 «모른다»를
 *    다르게 그린다)이 무너진다 — 1시간 미만은 **분으로** 말한다.
 */
function avgText(hours: number): { value: string; unit: string } {
  if (hours >= 1) return { value: String(hours), unit: '시간' };
  const mins = Math.round(hours * 60);
  return mins >= 1 ? { value: String(mins), unit: '분' } : { value: '1', unit: '분 미만' };
}

export function BriefingPage() {
  const [data, setData] = useState<BriefingDto | null>(null);
  const [state, setState] = useState<State>('loading');

  const load = useCallback(() => {
    setState('loading');
    getBriefing()
      .then((d) => { setData(d); setState('ok'); })
      .catch((e: { response?: { status?: number } }) => {
        setState(e?.response?.status === 403 ? 'forbidden' : 'failed');
      });
  }, []);

  useEffect(() => { load(); }, [load]);

  const submitRate = data && data.reports.headcount
    ? Math.round((data.reports.submitters / data.reports.headcount) * 100)
    : null;

  return (
    <div className="hrpage bfg">
      <header className="hrpage__head">
        <h1>대표 업무보고</h1>
        <p>
          {data ? `${data.period.from} ~ ${data.period.to}` : '이번 달'}
          {' · '}회사 전체
        </p>
      </header>

      {state === 'loading' && <div className="hrpage__note">불러오는 중…</div>}
      {state === 'forbidden' && (
        <div className="hrpage__note">이 화면은 <b>대표 또는 관리자</b>만 볼 수 있습니다.</div>
      )}
      {state === 'failed' && (
        <div className="hrpage__note">
          불러오지 못했습니다.{' '}
          <button type="button" className="bfg__retry" onClick={load}>다시 시도</button>
        </div>
      )}

      {state === 'ok' && data && (
        <>
          {/* ── 결재 — 대표가 가장 먼저 묻는 것 ───────────────────── */}
          <section className="hrpage__panel">
            <h2 className="hrpage__panelt">결재</h2>
            <div className="hrpage__kpis">
              <div className={`hrpage__kpi${data.approval.waitingOnMe > 0 ? ' is-warn' : ''}`}>
                <span className="hrpage__kpil">내가 처리할 것</span>
                <span className="hrpage__kpiv">{nf(data.approval.waitingOnMe)}<small>건</small></span>
                <span className="hrpage__kpid">지금 내 차례인 문서</span>
              </div>
              <div className="hrpage__kpi">
                <span className="hrpage__kpil">진행 중</span>
                <span className="hrpage__kpiv">{nf(data.approval.inProgress)}<small>건</small></span>
                <span className="hrpage__kpid">회사 전체</span>
              </div>
              <div className="hrpage__kpi">
                <span className="hrpage__kpil">종결</span>
                <span className="hrpage__kpiv">{nf(data.approval.completed)}<small>건</small></span>
                <span className="hrpage__kpid">이 기간에 끝난 것</span>
              </div>
              <div className="hrpage__kpi is-acc">
                <span className="hrpage__kpil">평균 처리 시간</span>
                {/* ★ 종결된 문서가 없으면 «0시간» 이 아니라 «—» 다 — 없는 것과 모르는 것은 다르다. */}
                <span className="hrpage__kpiv">
                  {data.approval.avgHoursToComplete === null
                    ? <span className="bfg__unknown">—</span>
                    : (() => {
                        const a = avgText(data.approval.avgHoursToComplete);
                        return <>{a.value}<small>{a.unit}</small></>;
                      })()}
                </span>
                <span className="hrpage__kpid">
                  {data.approval.avgHoursToComplete === null ? '종결된 문서가 없습니다' : '기안 → 종결'}
                </span>
              </div>
            </div>
          </section>

          {/* ── 업무 보고 ─────────────────────────────────────────── */}
          <section className="hrpage__panel">
            <h2 className="hrpage__panelt">업무 보고</h2>
            <div className="hrpage__kpis">
              <div className="hrpage__kpi is-acc">
                <span className="hrpage__kpil">제출률</span>
                <span className="hrpage__kpiv">
                  {submitRate === null
                    ? <span className="bfg__unknown">—</span>
                    : <>{submitRate}<small>%</small></>}
                </span>
                <span className="hrpage__kpid">
                  {data.reports.headcount === null
                    ? '재직자를 세지 못했습니다'
                    : `${nf(data.reports.submitters)}명 / 재직 ${nf(data.reports.headcount)}명`}
                </span>
              </div>
              {(['DAILY', 'WEEKLY', 'MONTHLY'] as const).map((k) => (
                <div className="hrpage__kpi" key={k}>
                  <span className="hrpage__kpil">
                    {k === 'DAILY' ? '일일' : k === 'WEEKLY' ? '주간' : '월간'}
                  </span>
                  <span className="hrpage__kpiv">{nf(data.reports.byKind[k])}<small>건</small></span>
                  <span className="hrpage__kpid">&nbsp;</span>
                </div>
              ))}
            </div>

            {data.reports.byTeam.length > 0 ? (
              <table className="bfg__table">
                <caption className="bfg__cap">
                  팀별 — <b>보고를 낸 그때의 소속</b>입니다(사람이 팀을 옮겨도 그때 그 보고는 그 팀 것입니다).
                </caption>
                <thead>
                  <tr><th scope="col">팀</th><th scope="col">건수</th><th scope="col">낸 사람</th></tr>
                </thead>
                <tbody>
                  {data.reports.byTeam.map((t) => (
                    <tr key={t.teamId ?? 'none'}>
                      <td>{t.teamId === null ? <span className="bfg__unknown">소속 없음</span> : `팀 #${t.teamId}`}</td>
                      <td className="bfg__num">{nf(t.count)}</td>
                      <td className="bfg__num">{nf(t.submitters)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className="bfg__empty">이 기간에 올라온 업무 보고가 없습니다.</p>
            )}
          </section>

          {/* ── 근태 · 연차 ───────────────────────────────────────── */}
          <section className="hrpage__panel">
            <h2 className="hrpage__panelt">근태 · 연차</h2>
            <div className="hrpage__kpis">
              <div className="hrpage__kpi">
                <span className="hrpage__kpil">출근한 날</span>
                <span className="hrpage__kpiv">{nf(data.attendance.workedDays)}<small>일</small></span>
                {/* ★ 「결근」은 세지 않는다 — 어느 날이 근무일인지 모른다(근태 판 F 의 규율). */}
                <span className="hrpage__kpid">기록된 것만 셉니다</span>
              </div>
              <div className={`hrpage__kpi${data.attendance.openDays > 0 ? ' is-warn' : ''}`}>
                <span className="hrpage__kpil">안 닫힌 날</span>
                <span className="hrpage__kpiv">{nf(data.attendance.openDays)}<small>일</small></span>
                <span className="hrpage__kpid">퇴근을 안 찍은 기록</span>
              </div>
              <div className="hrpage__kpi">
                <span className="hrpage__kpil">승인된 휴가</span>
                <span className="hrpage__kpiv">{nf(data.attendance.approvedLeaveDays)}<small>일</small></span>
                <span className="hrpage__kpid">올해 누계</span>
              </div>
              <div className="hrpage__kpi">
                <span className="hrpage__kpil">승인된 연장</span>
                <span className="hrpage__kpiv">{hoursText(data.attendance.approvedOvertimeMinutes)}</span>
                <span className="hrpage__kpid">이 기간</span>
              </div>
            </div>

            <div className="hrpage__kpis bfg__mt">
              <div className="hrpage__kpi">
                <span className="hrpage__kpil">부여</span>
                <span className="hrpage__kpiv">{nf(data.leave.grantedDays)}<small>일</small></span>
                <span className="hrpage__kpid">{nf(data.leave.countedOf)}명분</span>
              </div>
              <div className="hrpage__kpi">
                <span className="hrpage__kpil">사용</span>
                <span className="hrpage__kpiv">{nf(data.leave.usedDays)}<small>일</small></span>
                <span className="hrpage__kpid">&nbsp;</span>
              </div>
              <div className={`hrpage__kpi${data.leave.pendingDays > 0 ? ' is-warn' : ''}`}>
                <span className="hrpage__kpil">신청 중</span>
                <span className="hrpage__kpiv">{nf(data.leave.pendingDays)}<small>일</small></span>
                <span className="hrpage__kpid">결재를 기다리는 몫</span>
              </div>
              <div className="hrpage__kpi is-acc">
                <span className="hrpage__kpil">잔여</span>
                <span className="hrpage__kpiv">{nf(data.leave.remainingDays)}<small>일</small></span>
                <span className="hrpage__kpid">부여 − 사용</span>
              </div>
            </div>

            {/*
              ★★★ **부분합임을 말한다.** 못 센 사람을 0 으로 채우면 「그 사람 연차가 0」이라는
                 거짓이고, 통째로 비우면 아무것도 못 보여 준다. 「몇 명을 뺐다」가 정확한 진술이다.
            */}
            {data.leave.unknownQuota > 0 && (
              <p className="bfg__note" role="note">
                위 연차 숫자는 <b>{nf(data.leave.countedOf)}명분</b>입니다 —{' '}
                <b>{nf(data.leave.unknownQuota)}명</b>은 입사일도 부여 기록도 없어 세지 못했습니다.
              </p>
            )}
          </section>

          {/* ── 업무 · 인력 ─────────────────────────────────────── */}
          <section className="hrpage__panel">
            <h2 className="hrpage__panelt">업무 · 인력</h2>
            <div className="hrpage__kpis">
              <div className="hrpage__kpi is-acc">
                <span className="hrpage__kpil">업무 완료율</span>
                {/* ★ 할 일이 하나도 없으면 「0%」가 아니라 «—» 다. */}
                <span className="hrpage__kpiv">
                  {data.work.donePercent === null
                    ? <span className="bfg__unknown">—</span>
                    : <>{data.work.donePercent}<small>%</small></>}
                </span>
                <span className="hrpage__kpid">
                  {data.work.tasks === 0
                    ? '등록된 할 일이 없습니다'
                    : `${nf(data.work.done)} / ${nf(data.work.tasks)}건 · 프로젝트 ${nf(data.work.projects)}`}
                </span>
              </div>
              <div className={`hrpage__kpi${data.work.overdue > 0 ? ' is-warn' : ''}`}>
                <span className="hrpage__kpil">지연</span>
                <span className="hrpage__kpiv">{nf(data.work.overdue)}<small>건</small></span>
                {/* ★ 기한이 없는 일은 지연일 수 없다 — 화면이 그 뜻을 적는다. */}
                <span className="hrpage__kpid">기한이 지났는데 안 끝난 것</span>
              </div>
              <div className="hrpage__kpi">
                <span className="hrpage__kpil">입사 · 퇴사</span>
                <span className="hrpage__kpiv">
                  {nf(data.people.joined)}<small>명</small>
                  {' / '}{nf(data.people.leaving)}<small>명</small>
                </span>
                <span className="hrpage__kpid">재직 {nf(data.people.headcount)}명 · 이 기간</span>
              </div>
              <div className="hrpage__kpi">
                <span className="hrpage__kpil">발령</span>
                <span className="hrpage__kpiv">{nf(data.people.assignments)}<small>건</small></span>
                <span className="hrpage__kpid">이 기간에 효력이 생긴 것</span>
              </div>
            </div>
          </section>

          {/* ── 평가 · 교육 · 예약 ───────────────────────────────── */}
          <section className="hrpage__panel">
            <h2 className="hrpage__panelt">평가 · 교육 · 예약</h2>
            <div className="hrpage__kpis">
              {/*
                ★★★ **회차가 0 인 것은 「0%」가 아니다.** 0 으로 그리면 대표가
                   「우리 회사 평가 제출률이 0%」로 읽는데, 실은 **시작조차 안 한** 것이다.
              */}
              <div className="hrpage__kpi">
                <span className="hrpage__kpil">평가 제출률</span>
                <span className="hrpage__kpiv">
                  {data.evaluation.percent === null
                    ? <span className="bfg__unknown">—</span>
                    : <>{data.evaluation.percent}<small>%</small></>}
                </span>
                <span className="hrpage__kpid">
                  {data.evaluation.rounds === 0
                    ? '평가를 아직 시작하지 않았습니다'
                    : `${data.evaluation.inProgressRound} · ${nf(data.evaluation.submitted ?? 0)} / ${nf(data.evaluation.targets ?? 0)}명`}
                </span>
              </div>
              <div className={`hrpage__kpi${(data.education.notCompleted ?? 0) > 0 ? ' is-warn' : ''}`}>
                <span className="hrpage__kpil">필수 교육 미이수</span>
                <span className="hrpage__kpiv">
                  {data.education.notCompleted === null
                    ? <span className="bfg__unknown">—</span>
                    : <>{nf(data.education.notCompleted)}<small>명</small></>}
                </span>
                <span className="hrpage__kpid">
                  {data.education.courses === 0
                    ? '등록된 과정이 없습니다'
                    : `법정 과정 ${nf(data.education.statutoryCourses ?? 0)}개 기준`}
                </span>
              </div>
              <div className="hrpage__kpi">
                <span className="hrpage__kpil">예약 확정</span>
                <span className="hrpage__kpiv">{nf(data.reservation.confirmed)}<small>건</small></span>
                <span className="hrpage__kpid">
                  {data.reservation.pending > 0 ? `대기 ${nf(data.reservation.pending)}건` : '대기 없음'}
                </span>
              </div>
              <div className="hrpage__kpi">
                <span className="hrpage__kpil">예약된 시간</span>
                <span className="hrpage__kpiv">{hoursText(data.reservation.bookedMinutes)}</span>
                <span className="hrpage__kpid">확정된 것만 셉니다</span>
              </div>
            </div>
          </section>

          {/*
            ★★★ **못 하는 것을 «적어서» 보여 준다** — 빈 그래프를 그리지 않는다.
               하네스의 `gaps` 와 같은 규율이다: 없는 것을 «없다»고 적어 두면 할 일 목록이 된다.
          */}
          {data.notAvailable.length > 0 && (
            <section className="hrpage__panel">
              <h2 className="hrpage__panelt">아직 셀 수 없는 것</h2>
              <ul className="bfg__na">
                {data.notAvailable.map((s) => <li key={s}>{s}</li>)}
              </ul>
            </section>
          )}
        </>
      )}
    </div>
  );
}
